import { choiceNames, type MasterItem, type MasterKind, masterKind } from '@sj/shared';
import type { ReactNode } from 'react';
import { useSession } from '../app/session.js';

// Pilihan dari Data Master (tabel master_items) — satu-satunya sumber daftar pilihan di form. Nilai lama yang pilihannya
// sudah dinonaktifkan tetap ditampilkan agar form ubah tidak kehilangan nilainya.

/** Nama pilihan aktif satu jenis (+ nilai lama `keep`). */
export function useChoices(kind: MasterKind, keep?: string | null): string[] {
  return choiceNames(useSession().meta.master, kind, keep);
}

/** Pilihan aktif lengkap (dengan isian tambahan) satu jenis — mis. rekening. */
export function useMasterItems(kind: MasterKind): MasterItem[] {
  return (useSession().meta.master ?? []).filter((x) => x.kind === kind);
}

/** Isian <select> dari Data Master lengkap dengan labelnya. `empty` = teks pilihan kosong (opsional); tanpa `empty`
 *  pilihan wajib. `cls` = kelas tambahan pembungkus (mis. span2 / hidden). */
export function ChoiceSelect({
  kind,
  name,
  label,
  hint,
  cls = '',
  wrapId,
  value,
  empty,
  required,
}: {
  kind: MasterKind;
  name: string;
  label: ReactNode;
  hint?: string;
  cls?: string;
  wrapId?: string;
  value?: string | null;
  empty?: string;
  required?: boolean;
}) {
  const names = useChoices(kind, value);
  const id = `ch_${name}`;
  return (
    <div className={`f${cls ? ` ${cls}` : ''}`} id={wrapId}>
      <label htmlFor={id}>
        {label}
        {hint ? <span className="hint">{` ${hint}`}</span> : null}
      </label>
      <select
        className="inp"
        id={id}
        name={name}
        defaultValue={value ?? (empty !== undefined ? '' : names[0])}
        required={required}
      >
        {empty !== undefined || !names.length ? <option value="">{names.length ? empty : '— belum ada pilihan —'}</option> : null}
        {names.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </select>
      {names.length ? null : <span className="hint">{`Tambahkan ${masterKind(kind)?.label ?? ''} di menu Data Master.`}</span>}
    </div>
  );
}

/** Rekening tujuan transfer (Data Master → Rekening transfer). Tidak tampil bila belum ada rekening aktif. */
export function BankSelect({ name, label = 'Rekening tujuan' }: { name: string; label?: string }) {
  const banks = useMasterItems('bank');
  if (!banks.length) return null;
  const id = `ch_${name}`;
  return (
    <div className="f">
      <label htmlFor={id}>{label}</label>
      <select className="inp" id={id} name={name} required defaultValue={banks.length === 1 ? String(banks[0]?.id) : ''}>
        {banks.length > 1 ? <option value="">— pilih rekening —</option> : null}
        {banks.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
    </div>
  );
}
